import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

const UserPreferencesContext = createContext();

export function useUserPreferences() {
  return useContext(UserPreferencesContext);
}

const DEFAULT_PREFERENCES = {
  mode: 'light',
  customColors: {
    primary: '#1B365D',
    secondary: '#78B6CB'
  },
  showDailyQuote: true,
  sounds: {
    habitComplete: 'pop',
    perfectDay: 'success',
    notification: 'bell',
    taskComplete: 'pop',
    goalComplete: 'achievement',
    challengeDayComplete: 'pop',
    challengeComplete: 'achievement'
  },
  highlightToday: true,
  highlightPerfectDay: true,
  calendarComponentsOrder: ['progress', 'donuts', 'area', 'stats']
};

export function UserPreferencesProvider({ children }) {
  const { currentUser } = useAuth();
  const [preferences, setPreferences] = useState(() => {
    // Tenta recuperar as preferências do localStorage primeiro
    const savedPrefs = localStorage.getItem('userPreferences');
    if (savedPrefs) {
      const parsedPrefs = JSON.parse(savedPrefs);
      // Garante que a estrutura básica exista
      return {
        ...DEFAULT_PREFERENCES,
        ...parsedPrefs,
        customColors: {
          ...DEFAULT_PREFERENCES.customColors,
          ...(parsedPrefs.customColors || {})
        },
        sounds: {
          ...DEFAULT_PREFERENCES.sounds,
          ...(parsedPrefs.sounds || {})
        }
      };
    }
    return DEFAULT_PREFERENCES;
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadPreferences();
  }, [currentUser]);

  // Salva as preferências no localStorage sempre que elas mudarem
  useEffect(() => {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
  }, [preferences]);

  const loadPreferences = async () => {
    if (!currentUser) {
      setPreferences(DEFAULT_PREFERENCES);
      setIsLoading(false);
      return;
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists() && userDoc.data().preferences) {
        const userPrefs = userDoc.data().preferences;
        
        // Garantir que a estrutura de preferências esteja correta
        const mergedPrefs = {
          ...DEFAULT_PREFERENCES,  // Começa com todos os valores padrão
          ...userPrefs,  // Sobrescreve com os valores do usuário
          customColors: {
            ...DEFAULT_PREFERENCES.customColors,
            ...(userPrefs.customColors || {})
          },
          sounds: {
            ...DEFAULT_PREFERENCES.sounds,
            ...(userPrefs.sounds || {})
          },
          mode: userPrefs.mode || DEFAULT_PREFERENCES.mode  // Garante que o modo sempre exista
        };

        setPreferences(mergedPrefs);
      } else {
        setPreferences(DEFAULT_PREFERENCES);
      }
    } catch (error) {
      console.error('Erro ao carregar preferências:', error);
      setPreferences(DEFAULT_PREFERENCES);
    } finally {
      setIsLoading(false);
    }
  };

  const updatePreferences = async (newPreferences) => {
    if (!currentUser) return;

    try {
      // Garantir que a estrutura de preferências seja mantida ao atualizar
      const updatedPrefs = {
        ...preferences,  // Mantém as preferências atuais como base
        ...newPreferences,  // Aplica as novas preferências
        customColors: {
          ...preferences.customColors,
          ...(newPreferences.customColors || {})
        },
        sounds: {
          ...preferences.sounds,
          ...(newPreferences.sounds || {})
        },
        mode: newPreferences.mode || preferences.mode  // Garante que o modo seja atualizado corretamente
      };

      // Primeiro atualiza o estado local
      setPreferences(updatedPrefs);
      
      // Depois salva no localStorage
      localStorage.setItem('userPreferences', JSON.stringify(updatedPrefs));
      
      // Por último, atualiza no Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        preferences: updatedPrefs
      });
    } catch (error) {
      console.error('Erro ao atualizar preferências:', error);
      // Em caso de erro, tenta restaurar do localStorage
      const savedPrefs = localStorage.getItem('userPreferences');
      if (savedPrefs) {
        setPreferences(JSON.parse(savedPrefs));
      }
    }
  };

  const resetPreferences = async () => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      
      // Primeiro, atualiza o estado local
      setPreferences(DEFAULT_PREFERENCES);
      
      // Depois atualiza no Firestore
      await updateDoc(userRef, {
        preferences: DEFAULT_PREFERENCES
      });

      // Força uma atualização no localStorage também
      localStorage.setItem('userPreferences', JSON.stringify(DEFAULT_PREFERENCES));
      
    } catch (error) {
      console.error('Erro ao resetar preferências:', error);
      // Em caso de erro, tenta restaurar do localStorage
      const savedPrefs = localStorage.getItem('userPreferences');
      if (savedPrefs) {
        setPreferences(JSON.parse(savedPrefs));
      }
    }
  };

  const value = {
    preferences,
    updatePreferences,
    resetPreferences,
    isLoading
  };

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
} 